import { useEstablishmentAction } from './useActions'
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  InputAdornment,
  styled,
  Typography,
  Chip,
  Card,
  CardContent
} from '@mui/material'
import InputBase from 'design-system/input/base'
import React, { useEffect, useMemo, useState } from 'react'
import EmailIcon from '@mui/icons-material/Email'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ButtonBase from 'design-system/button'
import NumberInput, { InputMask } from 'components/InputMask'
import ConfirmationModal from 'components/ConfirmationModal'
import {
  PermissionByAdminRole,
  useSuperAdmin
} from 'components/rules/PermissionByRole'
import { ESTABLISHMENTS } from 'navigation/CONSTANTS'
import NavLinkBack from 'components/NavLinkBack'
import SelectBox from 'components/SelectBox'
import { useToast } from 'services/context/toast'
import {
  createHospitalCredentials,
  getHospitalCredentials,
  revokeHospitalCredentials,
  rotateHospitalCredentials
} from 'services/establishment/establishmentService'
import {
  IHospitalCredentialKeyResponse,
  IHospitalCredentialResponse
} from 'types/establishment/types'
import PermissionModal, {
  AVAILABLE_MODULES
} from '../components/PermissionModal'
import LogoUpload from './components/LogoUpload'

const StyledBox = styled(Box)(() => ({
  maxWidth: '1200px',
  padding: '20px 10% 90px 10%',
  justifyContent: 'center',
  alignSelf: 'center'
}))

const StyledTitle = styled(Typography)(({ theme }) => ({
  fontSize: '14pt',
  color: theme.palette.text.primary
}))

const EstablishmentActionPage: React.FC = () => {
  const {
    change,
    data,
    editMode,
    errorsMap,
    onSubmit,
    state,
    disableFields,
    showStreetAddress,
    enableButton,
    logoPreview,
    logoLoading,
    handleSelectLogo,
    handleRemoveLogo,
    establishmentId
  } = useEstablishmentAction()

  const [permissionModalOpen, setPermissionModalOpen] = useState(false)

  const toast = useToast()
  const isSuperAdmin = useSuperAdmin()

  const [credential, setCredential] =
    useState<IHospitalCredentialResponse | null>(null)

  const [generatedCredential, setGeneratedCredential] =
    useState<IHospitalCredentialKeyResponse | null>(null)

  const [credentialLoading, setCredentialLoading] = useState(false)

  const [credentialLoadError, setCredentialLoadError] = useState(false)

  const [credentialConfirmation, setCredentialConfirmation] = useState<
    'rotate' | 'revoke' | null
  >(null)

  const permissions = useMemo(() => {
    return data.modules_permission || []
  }, [data.modules_permission])

  const loadHospitalCredentials = async () => {
  if (!editMode || !establishmentId) return

  setCredentialLoadError(false)
  setCredentialLoading(true)

  const response = await getHospitalCredentials(establishmentId)

  if (response.success && response.data) {
    setCredential(response.data)
  } else {
    setCredentialLoadError(true)
    toast.handler({
      content: 'Houve um erro ao carregar as credenciais de integração.',
      severity: 'error'
    })
  }

  setCredentialLoading(false)
}

  useEffect(() => {
    if (!isSuperAdmin) return

    void loadHospitalCredentials()
  }, [isSuperAdmin, editMode, establishmentId])

  const copyCredentialValue = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value)

      toast.handler({
        content: 'Copiado para a área de transferência.',
        severity: 'success'
      })
    } catch {
      toast.handler({
        content: 'Não foi possível copiar para a área de transferência.',
        severity: 'error'
      })
    }
  }

  const handleCreateCredential = async () => {
    if (!establishmentId) return

    setCredentialLoading(true)

    const response = await createHospitalCredentials(establishmentId)

    if (response.success && response.data) {
      setGeneratedCredential(response.data)

      await loadHospitalCredentials()

      toast.handler({
        content: 'Credencial criada com sucesso.',
        severity: 'success'
      })
    } else {
      toast.handler({
        content: 'Houve um erro ao criar a credencial.',
        severity: 'error'
      })

      setCredentialLoading(false)
    }
  }

  const handleRotateCredential = async () => {
    if (!establishmentId) return

    setCredentialConfirmation(null)
    setCredentialLoading(true)

    const response = await rotateHospitalCredentials(establishmentId)

    if (response.success && response.data) {
      setGeneratedCredential(response.data)

      await loadHospitalCredentials()

      toast.handler({
        content: 'Credencial rotacionada com sucesso.',
        severity: 'success'
      })
    } else {
      toast.handler({
        content: 'Houve um erro ao rotacionar a credencial.',
        severity: 'error'
      })

      setCredentialLoading(false)
    }
  }

  const handleRevokeCredential = async () => {
    if (!establishmentId) return

    setCredentialConfirmation(null)
    setCredentialLoading(true)

    const response = await revokeHospitalCredentials(establishmentId)

    if (response.success) {
      setGeneratedCredential(null)

      await loadHospitalCredentials()

      toast.handler({
        content: 'Credencial revogada com sucesso.',
        severity: 'success'
      })
    } else {
      toast.handler({
        content: 'Houve um erro ao revogar a credencial.',
        severity: 'error'
      })

      setCredentialLoading(false)
    }
  }

  const credentialFallback = credentialLoadError ? (
    <Box>
      <Typography variant='body2' color='error' mb={2}>
        Não foi possível carregar as credenciais de integração.
      </Typography>
      <ButtonBase
        variant='outlined'
        onClick={loadHospitalCredentials}
        disabled={credentialLoading}
        loading={credentialLoading}
      >
        Tentar novamente
      </ButtonBase>
    </Box>
  ) : (
    <Typography variant='body2' color='text.secondary'>
      Carregando credenciais...
    </Typography>
  )

  return (
    <StyledBox>
      <Grid container>
        <Grid size={12} paddingBlock={2}>
          <NavLinkBack to={`/${ESTABLISHMENTS}`} title='Estabelecimentos' />
        </Grid>
      </Grid>
      <Typography variant='h5' padding={'20px 0px'}>
        {!editMode ? 'Cadastro de estabelecimento' : 'Alterar estabelecimento'}
      </Typography>
      <Grid container justifyContent={'center'} spacing={4}>
        <Grid spacing={2} container flexDirection={'row'}>
          <Grid size={12}>
            <Grid size={{ md: 3, sm: 4, xs: 12 }}>
              <SelectBox
                label='Tipo de estabelecimento'
                disabled={disableFields}
                menuItems={[
                  { label: 'Clínica', value: 'CLINIC' },
                  { label: 'Hospital', value: 'HOSPITAL' },
                  { label: 'Laboratório', value: 'LABORATORY' },
                  { label: 'Farmácia', value: 'PHARMACY' },
                  { label: 'Outro', value: 'OTHER' }
                ]}
                control={[
                  data.establishment_type,
                  value => change('establishment_type', value)
                ]}
              />
            </Grid>
            <FormControlLabel
              disabled={disableFields}
              control={
                <Checkbox
                  checked={data.active}
                  onChange={(_, checked) => change('active', checked)}
                />
              }
              label='Ativo'
            />
          </Grid>
          <Grid size={12}>
            <LogoUpload
              value={logoPreview}
              disabled={disableFields}
              loading={logoLoading}
              onSelect={handleSelectLogo}
              onRemove={handleRemoveLogo}
            />
          </Grid>
          <Grid size={{ sm: 6, xs: 12 }}>
            <InputBase
              label='Razão social'
              required
              variant='outlined'
              value={data.establishment_name}
              disabled={disableFields}
              onChange={e => change('establishment_name', e.target.value)}
              error={errorsMap.has('establishment_name')}
              helperText={errorsMap.get('establishment_name')}
            />
          </Grid>
          <Grid size={{ sm: 6, xs: 12 }}>
            <InputBase
              label='Nome fantasia'
              required
              variant='outlined'
              value={data.commercial_name}
              onChange={e => change('commercial_name', e.target.value)}
              disabled={disableFields}
              error={errorsMap.has('commercial_name')}
              helperText={errorsMap.get('commercial_name')}
            />
          </Grid>
          <Grid size={{ sm: 6, xs: 12 }}>
            <InputMask
              placeholder='00.000.000/0000-00'
              label='CNPJ'
              value={data.cnpj}
              mask='00.000.000/0000-00'
              onChange={(_, value) => change('cnpj', value)}
              disabled={disableFields}
              error={errorsMap.has('cnpj')}
              required
              helperText={errorsMap.get('cnpj')}
            />
          </Grid>
          <Grid size={{ sm: 6, xs: 12 }}>
            <InputMask
              placeholder='0000000'
              label='CNES'
              value={data.cnes}
              required
              mask='0000000'
              onChange={(_, value) => change('cnes', value)}
              disabled={disableFields}
              error={errorsMap.has('cnes')}
              helperText={errorsMap.get('cnes')}
            />
          </Grid>

          <Grid size={{ sm: 3, xs: 6 }}>
            <InputMask
              placeholder='00000-000'
              label='CEP'
              value={data.postal_code}
              required
              mask='00000-000'
              onChange={(_, value) => change('postal_code', value)}
              disabled={disableFields}
              error={errorsMap.has('postal_code')}
              helperText={errorsMap.get('postal_code')}
            />
          </Grid>
          {showStreetAddress && (
            <Grid container>
              <Grid size={{ sm: 9, xs: 12 }}>
                <InputBase
                  label='Logradouro'
                  required
                  variant='outlined'
                  value={data.street_address}
                  disabled
                />
              </Grid>
              <Grid size={{ sm: 3, xs: 4 }}>
                <NumberInput
                  label='Número'
                  required
                  variant='outlined'
                  control={[data.number, value => change('number', value)]}
                  mask={'\\d+'}
                  disabled={disableFields}
                  error={errorsMap.has('number')}
                  helperText={errorsMap.get('number')}
                />
              </Grid>
              <Grid size={{ sm: 5, xs: 8 }}>
                <InputBase
                  label='Complemento'
                  variant='outlined'
                  value={data.complement}
                  onChange={e => change('complement', e.target.value)}
                  disabled={disableFields}
                  error={errorsMap.has('complement')}
                  helperText={errorsMap.get('complement')}
                />
              </Grid>
              <Grid size={{ sm: 5, xs: 8 }}>
                <InputBase
                  label='Cidade'
                  required
                  variant='outlined'
                  value={data.city}
                  disabled
                />
              </Grid>
              <Grid size={{ sm: 2, xs: 4 }}>
                <InputBase
                  label='Estado'
                  required
                  variant='outlined'
                  value={data.state}
                  disabled
                />
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid size={12}>
          <StyledTitle>Contato</StyledTitle>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <InputMask
            mask='(00) 00000-0000'
            sx={{ backgroundColor: 'background.paper' }}
            label='Telefone'
            required
            placeholder='(99) 99999-9999'
            value={data.phone}
            onChange={(_, value) => change('phone', value)}
            disabled={disableFields}
            error={errorsMap.has('phone')}
            helperText={errorsMap.get('phone')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <InputBase
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position='start'>
                    <EmailIcon />
                  </InputAdornment>
                ),
                autoComplete: 'off'
              }
            }}
            placeholder='exemplo@email.com'
            label='Email'
            required
            variant='outlined'
            value={data.email}
            onChange={e => change('email', e.target.value)}
            disabled={disableFields}
            error={errorsMap.has('email')}
            helperText={errorsMap.get('email')}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 4 }}>
        <Grid size={12}>
          <StyledTitle>Permissões do Estabelecimento</StyledTitle>
        </Grid>
        <Grid size={12}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box
                display='flex'
                justifyContent='space-between'
                alignItems='center'
                mb={2}
              >
                <Typography variant='body1' color='text.secondary'>
                  Configure os módulos que este estabelecimento terá acesso:
                </Typography>
                <ButtonBase
                  variant='outlined'
                  size='small'
                  onClick={() => setPermissionModalOpen(true)}
                  disabled={disableFields}
                >
                  Gerenciar Permissões
                </ButtonBase>
              </Box>

              {permissions.length > 0 ? (
                <Box>
                  <Typography variant='body2' fontWeight='bold' gutterBottom>
                    Módulos Selecionados ({permissions.length}):
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {permissions.map(moduleId => {
                      const module = AVAILABLE_MODULES.find(
                        m => m.id === moduleId
                      )
                      return module ? (
                        <Chip
                          key={moduleId}
                          label={module.name}
                          size='small'
                          color={module.color}
                          variant='filled'
                        />
                      ) : null
                    })}
                  </Box>
                </Box>
              ) : (
                <Typography
                  variant='body2'
                  color='text.secondary'
                  fontStyle='italic'
                >
                  Nenhum módulo selecionado. O estabelecimento não terá acesso a
                  funcionalidades específicas.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {editMode && establishmentId && (
        <PermissionByAdminRole>
          <Grid container spacing={2} sx={{ mt: 4 }}>
            <Grid size={12}>
              <StyledTitle>Credenciais de Integração</StyledTitle>
            </Grid>

            <Grid size={12}>
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  {credential ? (
                    <>
                      <Typography variant='body1' color='text.secondary' mb={2}>
                        Credenciais utilizadas para autenticar integrações.
                      </Typography>

                      <Box mb={2}>
                        <Typography variant='body2' fontWeight='bold'>
                          Hospital ID
                        </Typography>

                        <Box display='flex' alignItems='center' gap={1}>
                          <Typography variant='body2'>
                            {credential.hospital_id}
                          </Typography>

                          <IconButton
                            size='small'
                            onClick={() =>
                              copyCredentialValue(credential.hospital_id)
                            }
                          >
                            <ContentCopyIcon fontSize='small' />
                          </IconButton>
                        </Box>
                      </Box>

                      <Box mb={2}>
                        <Typography variant='body2' fontWeight='bold'>
                          Status
                        </Typography>

                        <Typography variant='body2'>
                          {credential.active
                            ? 'Credencial ativa'
                            : 'Sem credencial ativa'}
                        </Typography>
                      </Box>

                      {credential.created_at && (
                        <Box mb={2}>
                          <Typography variant='body2' fontWeight='bold'>
                            Criada em
                          </Typography>

                          <Typography variant='body2'>
                            {new Date(
                              credential.created_at
                            ).toLocaleString('pt-BR')}
                          </Typography>
                        </Box>
                      )}

                      {generatedCredential && (
                        <Alert severity='warning' sx={{ mb: 2 }}>
                          <Typography
                            variant='body2'
                            fontWeight='bold'
                            mb={1}
                          >
                            Salve esta chave agora.
                          </Typography>

                          <Typography variant='body2' mb={1}>
                            Ela não será exibida novamente após sair ou
                            recarregar esta página.
                          </Typography>

                          <Box display='flex' alignItems='center' gap={1}>
                            <Typography
                              variant='body2'
                              sx={{
                                wordBreak: 'break-all',
                                fontFamily: 'monospace'
                              }}
                            >
                              {generatedCredential.hospital_key}
                            </Typography>

                            <IconButton
                              size='small'
                              onClick={() =>
                                copyCredentialValue(
                                  generatedCredential.hospital_key
                                )
                              }
                            >
                              <ContentCopyIcon fontSize='small' />
                            </IconButton>
                          </Box>
                        </Alert>
                      )}

                      {!credential.active ? (
                        <ButtonBase
                          variant='contained'
                          onClick={handleCreateCredential}
                          disabled={credentialLoading}
                          loading={credentialLoading}
                        >
                          Criar credencial
                        </ButtonBase>
                      ) : (
                        <Box display='flex' gap={1} flexWrap='wrap'>
                          <ButtonBase
                            variant='outlined'
                            onClick={() =>
                              setCredentialConfirmation('rotate')
                            }
                            disabled={credentialLoading}
                          >
                            Gerar nova credencial
                          </ButtonBase>

                          <ButtonBase
                            variant='outlined'
                            color='warning'
                            onClick={() =>
                              setCredentialConfirmation('revoke')
                            }
                            disabled={credentialLoading}
                          >
                            Revogar credencial
                          </ButtonBase>
                        </Box>
                      )}
                    </>
                  ) : (
                    credentialFallback
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <ConfirmationModal
            open={credentialConfirmation === 'rotate'}
            title='Deseja gerar uma nova credencial? A chave atual deixará de funcionar imediatamente.'
            onConfirm={handleRotateCredential}
            onCancel={() => setCredentialConfirmation(null)}
          />

          <ConfirmationModal
            open={credentialConfirmation === 'revoke'}
            title='Deseja revogar esta credencial? A autenticação das integrações deixará de funcionar imediatamente.'
            onConfirm={handleRevokeCredential}
            onCancel={() => setCredentialConfirmation(null)}
          />
        </PermissionByAdminRole>
      )}

      <Grid size={12} justifyContent={'end'} container>
        <ButtonBase
          variant='contained'
          type='submit'
          sx={{ mt: 2 }}
          disabled={!enableButton || state == 'loading'}
          loading={state == 'loading'}
          onClick={onSubmit}
        >
          {editMode ? 'Editar estabelecimento' : 'Cadastrar estabelecimento'}
        </ButtonBase>
      </Grid>

      <PermissionModal
        permissionModalOpen={permissionModalOpen}
        selectedEstablishment={
          {
            establishment_id: '',
            establishment_name:
              data.establishment_name || 'Novo Estabelecimento',
            modules_permission: permissions
          } as any
        }
        onConfirm={modules => {
          change('modules_permission', modules)
          setPermissionModalOpen(false)
        }}
        onClose={() => setPermissionModalOpen(false)}
        disableApiCalls={true}
      />
    </StyledBox>
  )
}

export default EstablishmentActionPage
